// Primitive della documentazione API — estratte verbatim da DeveloperSettings.tsx.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2, Key, Shield, Eye, EyeOff, AlertTriangle, CheckCircle2, Download, FileText, AlertCircle, Server, Globe, Send, RefreshCw, Webhook, BookOpen, Code, Wrench, Puzzle, Lock, Pencil, Search, X, ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react';
import {
  ALL_AVAILABLE_SCOPES,
  SCOPE_CATEGORIES,
  SCOPE_TO_EVENTS_MAP,
  WEBHOOK_EVENT_CATEGORIES as SHARED_EVENT_CATEGORIES,
  getAllowedEventsForScopes,
} from '@easyflow/shared';
import { format } from 'date-fns';
import { HubShell } from '@/components/common/HubShell';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient as api, getPublicBaseUrl } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import MCPToolsTab from '@/components/mcp/MCPToolsTab';
import MCPGatewayTab from '@/components/mcp/MCPGatewayTab';
import IntegrationTemplatesTab from '@/components/mcp/IntegrationTemplatesTab';
import IdentitySelector from '@/components/ai/IdentitySelector';
import { useToast } from '@/hooks/use-toast';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';


export interface ApiDocSection {
  id: string;
  title: string;
  icon: LucideIcon;
  keywords?: string[];
}

export const API_DOC_SECTIONS: ApiDocSection[] = [
  { id: 'auth', title: 'Authentication', icon: Key, keywords: ['login', 'token', 'jwt', 'api key', 'reset password', 'reset-password', 'oauth', 'bearer'] },
  { id: 'stores', title: 'Stores API', icon: Server, keywords: ['negozi', 'tenant', 'shop'] },
  { id: 'trunks', title: 'Trunks API', icon: Globe, keywords: ['sip', 'gateway', 'trunk', 'pstn'] },
  { id: 'extensions', title: 'Extensions API', icon: Server, keywords: ['interni', 'blf', 'blf-list', 'busy lamp', 'sip user', 'phone', 'call forwarding', 'cf', 'voicemail user'] },
  { id: 'queues', title: 'Queues API', icon: Server, keywords: ['code', 'call queue', 'agent', 'acd'] },
  { id: 'ringgroups', title: 'Ring Groups API', icon: Server, keywords: ['gruppo squillo', 'ring group', 'hunt'] },
  { id: 'inbound', title: 'Inbound Routes API', icon: Globe, keywords: ['ingresso', 'did', 'numero entrante', 'inbound'] },
  { id: 'outbound', title: 'Outbound Routes API', icon: Globe, keywords: ['uscita', 'instradamento', 'outbound', 'dialplan'] },
  { id: 'ivr', title: 'IVR Menus API', icon: Puzzle, keywords: ['risponditore', 'menu vocale', 'auto attendant'] },
  { id: 'time-conditions', title: 'Time Conditions API', icon: Server, keywords: ['orari', 'fascia oraria', 'time group', 'business hours'] },
  { id: 'calls', title: 'Active Calls API', icon: Server, keywords: ['chiamate attive', 'live calls', 'channels', 'hangup'] },
  { id: 'recordings', title: 'Recordings API', icon: FileText, keywords: ['registrazioni', 'audio', 'recording', 'wav', 'mp3'] },
  { id: 'voicemail', title: 'Voicemail API', icon: Server, keywords: ['segreteria', 'messaggi vocali', 'mailbox'] },
  { id: 'cdr', title: 'CDR API', icon: FileText, keywords: ['report chiamate', 'storico', 'call detail record', 'log chiamate'] },
  { id: 'webhooks', title: 'Webhooks API', icon: Webhook, keywords: ['eventi', 'callback', 'subscription'] },
  { id: 'health', title: 'Health & Status', icon: CheckCircle2, keywords: ['stato', 'monitor', 'uptime', 'ping'] },
  { id: 'legacy-migration', title: 'Migrazione da integration-api', icon: Globe, keywords: ['legacy', 'integration-api', 'migrazione', 'aiAgentRef', 'ai-routing', 'mappatura'] },
  { id: 'errors', title: 'Errors & Rate Limiting', icon: AlertCircle, keywords: ['errori', 'rate limit', 'throttle', 'codici errore', 'http status'] },
  { id: 'mcp-tools-overview', title: 'MCP Tools Overview', icon: Wrench, keywords: ['mcp', 'tool', 'strumenti', 'function calling'] },
  { id: 'mcp-agent-bindings', title: 'Agent Bindings', icon: Shield, keywords: ['mcp', 'agent', 'binding', 'permessi agent'] },
  { id: 'mcp-workflows', title: 'Workflows & Actions', icon: Code, keywords: ['mcp', 'workflow', 'azioni', 'automation'] },
  { id: 'mcp-booking-otp', title: 'Booking OTP (WhatsApp)', icon: Shield, keywords: ['mcp', 'otp', 'whatsapp', 'verifica', 'booking otp'] },
  { id: 'integration-google', title: 'Google Integration', icon: Globe, keywords: ['google', 'calendar', 'gmail', 'oauth google', 'workspace'] },
  { id: 'integration-twilio', title: 'Twilio Integration', icon: Send, keywords: ['twilio', 'sms', 'whatsapp', 'video call', 'messaging'] },
  { id: 'phone-provisioning', title: 'Phone Provisioning', icon: Server, keywords: ['provisioning', 'yealink', 'fanvil', 'config telefono', 'auto-provision'] },
  { id: 'blf-config', title: 'BLF Configuration', icon: Eye, keywords: ['blf', 'blf-list', 'busy lamp field', 'tasti rapidi', 'speed dial'] },
  { id: 'phonebook-xml', title: 'Remote Phonebook', icon: BookOpen, keywords: ['rubrica', 'phonebook', 'xml', 'contatti telefono'] },
  { id: 'calendar-appointments', title: 'Calendar / Appointments', icon: Server, keywords: ['calendario', 'appuntamenti', 'appointment', 'slot', 'consulenti'] },
  { id: 'public-booking', title: 'Public Booking API', icon: Globe, keywords: ['prenotazione pubblica', 'booking pubblico', 'slug', 'cancel', 'reschedule'] },
  { id: 'admin-booking-forms', title: 'Booking Forms (Admin)', icon: Server, keywords: ['form prenotazione', 'booking form', 'admin booking', 'campi form'] },
];

export function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, { bg: string; fg: string; border: string }> = {
    GET:    { bg: 'rgba(59,130,246,0.14)',  fg: '#2563EB', border: 'rgba(59,130,246,0.30)' },
    POST:   { bg: 'rgba(16,185,129,0.14)',  fg: '#059669', border: 'rgba(16,185,129,0.30)' },
    PUT:    { bg: 'rgba(245,158,11,0.16)',  fg: '#B45309', border: 'rgba(245,158,11,0.32)' },
    PATCH:  { bg: 'rgba(245,158,11,0.16)',  fg: '#B45309', border: 'rgba(245,158,11,0.32)' },
    DELETE: { bg: 'rgba(239,68,68,0.14)',   fg: '#B91C1C', border: 'rgba(239,68,68,0.30)' },
  };
  const c = colors[method] || { bg: 'rgba(148,163,184,0.18)', fg: '#475569', border: 'rgba(148,163,184,0.30)' };
  return (
    <span
      className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10.5px] font-bold tracking-wide"
      style={{
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        fontFamily: aurora.mono,
        minWidth: 56,
      }}
    >
      {method}
    </span>
  );
}

export function Endpoint({ method, path, desc, scope }: { method: string; path: string; desc: string; scope?: string }) {
  const { t } = useAuroraTheme();
  return (
    <div
      className="flex items-start gap-3 py-2.5 px-3 rounded-md transition-colors"
      style={{
        background: 'transparent',
        border: `1px solid transparent`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = t.panel2; e.currentTarget.style.borderColor = t.border; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <MethodBadge method={method} />
      <div className="min-w-0 flex-1">
        <code
          className="text-[12.5px] break-all"
          style={{ fontFamily: aurora.mono, color: t.text }}
        >
          {path}
        </code>
        <p
          className="text-[12px] mt-0.5"
          style={{ fontFamily: aurora.sans, color: t.muted }}
        >
          {desc}
        </p>
        {scope && (
          <span
            className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
            style={{
              background: `${t.ai}1A`,
              color: t.ai,
              border: `1px solid ${t.ai}33`,
              fontFamily: aurora.mono,
            }}
          >
            {scope}
          </span>
        )}
      </div>
    </div>
  );
}

export function CodeBlock({ children, title }: { children: string; title?: string }) {
  const { t } = useAuroraTheme();
  return (
    <div className="my-3">
      {title && (
        <p
          className="text-[11px] mb-1.5 uppercase tracking-[0.12em] font-semibold"
          style={{ color: t.muted, fontFamily: aurora.sans }}
        >
          {title}
        </p>
      )}
      <pre
        className="rounded-md p-3 text-[12px] overflow-x-auto whitespace-pre"
        style={{
          background: t.panel2,
          border: `1px solid ${t.border}`,
          color: t.text,
          fontFamily: aurora.mono,
          lineHeight: 1.55,
        }}
      >
        {children}
      </pre>
    </div>
  );
}

export function DocSection({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <motion.div
      id={`doc-${id}`}
      initial={aurora.fadeUp.initial}
      animate={aurora.fadeUp.animate}
      transition={aurora.fadeUp.transition}
      className="space-y-3"
    >
      {children}
    </motion.div>
  );
}

